import React, { useEffect, useRef, useState, useCallback } from 'react';
import { getCsrfToken } from './utils.js';
import { getCableConsumer } from '../../cable.js';

// Viewer-side attendance recorder.
//
// Two phases:
//   1. Camera setup — a click requests getUserMedia({ video, audio: false }),
//      the student previews and may switch cameras, then joins. Denial blocks
//      entry, since recording is a hard requirement for audit. Nothing is
//      recorded and no session is opened until they join.
//   2. Active recording — MediaRecorder emits ~10s WebM chunks; each chunk is
//      PUT to the chunk endpoint with retry/backoff. On unmount or tab close,
//      a finalize beacon is fired so the server can commit + cold-tier the
//      assembled blob even if the tab dies.
//
// This component renders its `children` (the actual room) only after consent
// is granted AND a recording session has been started server-side.

const CHUNK_INTERVAL_MS = 10_000;
const MAX_RETRIES = 3;
// After this many chunks fail end-to-end in a row, stop recording altogether.
// Per-chunk retries are bounded, but the SEQUENCE needs its own bound too:
// MediaRecorder mints a fresh chunk every interval forever, so a permanently
// broken upstream (locally: no Azure credentials, so every chunk 502s) becomes
// an endless PUT storm. Three consecutive failures is enough to tell a
// transient blip from a dead upstream.
const MAX_CONSECUTIVE_CHUNK_FAILURES = 3;
const VIDEO_BITS_PER_SECOND = 250_000;

// Abortable sleep — a pending backoff must not outlive the recorder.
function sleep(ms, signal) {
  return new Promise((resolve) => {
    if (signal?.aborted) return resolve();
    const t = setTimeout(resolve, ms);
    signal?.addEventListener('abort', () => { clearTimeout(t); resolve(); }, { once: true });
  });
}

function pickMimeType() {
  // VP8 has the broadest cross-browser support for MediaRecorder. VP9 yields
  // smaller files but isn't supported in some Safari versions.
  const candidates = [
    'video/webm;codecs=vp8',
    'video/webm;codecs=vp9',
    'video/webm',
  ];
  for (const t of candidates) {
    if (window.MediaRecorder && MediaRecorder.isTypeSupported(t)) return t;
  }
  return '';
}

// Upload a single chunk with exponential backoff. Returns true on success.
// `signal` aborts both the in-flight request and any pending backoff, so a
// shutdown doesn't leave a retry chain running against a torn-down recorder.
async function uploadChunk(url, index, blob, signal) {
  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    if (signal?.aborted) return false;
    try {
      const res = await fetch(`${url}?index=${index}`, {
        method: 'PUT',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/octet-stream',
          'X-CSRF-Token': getCsrfToken(),
        },
        body: blob,
        keepalive: blob.size < 64 * 1024, // best-effort survival of unloads for tiny chunks
        signal,
      });
      if (res.ok) return true;
      if (res.status === 413 || res.status === 409) return false; // not retryable
    } catch (_e) {
      if (signal?.aborted) return false; // aborted, not a network blip
    }
    // No sleep after the final attempt — it would only delay giving up.
    if (attempt < MAX_RETRIES - 1) await sleep(500 * 2 ** attempt, signal);
  }
  return false;
}

const VIDEO_CONSTRAINTS = { width: { ideal: 640 }, height: { ideal: 360 }, frameRate: { ideal: 15 } };

function stopStream(stream) {
  stream?.getTracks().forEach((t) => { try { t.stop(); } catch (_e) {} });
}

function cameraErrorMessage(e, { switching = false } = {}) {
  const code = e?.name || '';
  if (code === 'NotAllowedError' || code === 'SecurityError') {
    return 'Camera access was denied. This webinar requires your camera for attendance verification. Enable camera access in your browser settings and try again.';
  }
  if (code === 'NotFoundError' || code === 'OverconstrainedError') {
    return switching
      ? 'That camera is no longer available. Choose a different camera.'
      : 'No camera was detected on this device. A working camera is required to attend this webinar.';
  }
  if (code === 'NotReadableError') {
    return switching
      ? 'That camera is in use by another application. Choose a different camera or close the other app.'
      : 'Your camera is in use by another application. Close it and try again.';
  }
  return `Could not access your camera (${code || 'unknown error'}).`;
}

// Camera setup, before the room exists. Permission is requested only from a
// click, the resulting stream is previewed and switchable, and the exact stream
// the student approves is what the recorder receives — no second capture.
function CameraSetup({ onJoin, onBlocked }) {
  const [previewStream, setPreviewStream] = useState(null);
  const [devices, setDevices] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const streamRef = useRef(null);
  const hadPreviewRef = useRef(false);
  const busyRef = useRef(false);
  const joinedRef = useRef(false);
  const videoRef = useRef(null);

  useEffect(() => {
    if (videoRef.current && previewStream) videoRef.current.srcObject = previewStream;
  }, [previewStream]);

  // Preview tracks must not outlive this component. A full navigation discards
  // the document without guaranteeing effect cleanup runs, so pagehide is what
  // actually frees the camera on the way out; unmount covers in-app teardown.
  // Both funnel through one idempotent release, and handing off suppresses it
  // because the recorder owns the stream from then on.
  const releasePreview = useCallback(() => {
    if (joinedRef.current) return;
    const current = streamRef.current;
    streamRef.current = null;
    stopStream(current);
  }, []);

  // A restored document keeps React state but not the tracks pagehide stopped,
  // so a surviving preview would render a Join button that can never work.
  // Reset it back to something the student can act on.
  const resetDeadPreview = useCallback(() => {
    if (joinedRef.current || streamRef.current || !hadPreviewRef.current) return;
    hadPreviewRef.current = false;
    setPreviewStream(null);
    setDevices([]);
    setSelectedId('');
    setError(null);
    setNotice('Your camera preview was paused while you were away. Enable your camera again to continue.');
  }, []);

  useEffect(() => {
    window.addEventListener('pagehide', releasePreview);
    window.addEventListener('pageshow', resetDeadPreview);
    return () => {
      window.removeEventListener('pagehide', releasePreview);
      window.removeEventListener('pageshow', resetDeadPreview);
      releasePreview();
    };
  }, [releasePreview, resetDeadPreview]);

  const refreshDevices = useCallback(async () => {
    try {
      const list = (await navigator.mediaDevices.enumerateDevices()).filter((d) => d.kind === 'videoinput');
      setDevices(list);
      return list;
    } catch (_e) {
      return null;
    }
  }, []);

  const acquire = useCallback(async (deviceId) => {
    if (busyRef.current || joinedRef.current) return;
    if (!navigator.mediaDevices?.getUserMedia) {
      setError(window.isSecureContext === false
        ? 'This page must be served over HTTPS before the browser will allow camera access.'
        : 'This browser does not support camera capture. Use a current version of Chrome, Edge, Firefox or Safari.');
      return;
    }
    busyRef.current = true;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const next = await navigator.mediaDevices.getUserMedia({
        video: deviceId ? { ...VIDEO_CONSTRAINTS, deviceId: { exact: deviceId } } : VIDEO_CONSTRAINTS,
        audio: false,
      });
      // The working preview is released only after its replacement exists, so a
      // failed switch never leaves the student with a dead preview.
      const previous = streamRef.current;
      streamRef.current = next;
      hadPreviewRef.current = true;
      setPreviewStream(next);
      if (previous) stopStream(previous);
      // Labels are blank until permission is granted, so enumerate after.
      const list = await refreshDevices();
      // Only claim a device is selected when its id is actually in the list;
      // otherwise the select would show a value no option carries.
      const activeId = deviceId || next.getVideoTracks()[0]?.getSettings?.()?.deviceId || '';
      const known = Boolean(activeId) && (list || []).some((d) => d.deviceId === activeId);
      setSelectedId(known ? activeId : '');
    } catch (e) {
      setError(cameraErrorMessage(e, { switching: Boolean(deviceId) }));
      onBlocked?.(e?.name || 'unknown');
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }, [refreshDevices, onBlocked]);

  const join = useCallback(() => {
    if (joinedRef.current || busyRef.current || !streamRef.current) return;
    joinedRef.current = true;
    onJoin(streamRef.current);
  }, [onJoin]);

  return (
    <div className="max-w-xl mx-auto p-6">
      <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900">Camera required for attendance</h2>
        <p className="mt-2 text-sm text-gray-600">
          This webinar records a short video of your webcam throughout the session as proof of
          attendance. The recording is stored privately and reviewed only if your attendance is audited.
          No one watches it live and your camera feed is not shared with the host or other attendees.
        </p>
        <ul className="mt-3 text-xs text-gray-500 list-disc pl-5 space-y-1">
          <li>Video only — your microphone is not recorded.</li>
          <li>You can leave at any time; the recording stops when you close the page.</li>
        </ul>

        {previewStream && (
          <div className="mt-5">
            <video
              ref={videoRef}
              data-testid="camera-preview"
              muted
              autoPlay
              playsInline
              className="w-full aspect-video rounded-lg bg-gray-900 object-cover"
            />
            <p className="mt-2 text-xs text-gray-500">
              Preview only — nothing is being recorded yet. Recording starts when you join.
            </p>

            <label className="mt-4 block">
              <span className="block text-xs font-semibold tracking-wider text-gray-600 uppercase mb-1">Camera</span>
              <select
                data-testid="camera-select"
                value={selectedId}
                onChange={(e) => acquire(e.target.value)}
                disabled={busy || devices.length === 0}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 focus:outline-none disabled:bg-gray-50 disabled:text-gray-400"
              >
                {devices.length === 0 && <option value="">No cameras found</option>}
                {devices.length > 0 && selectedId === '' && <option value="">Current camera</option>}
                {devices.map((d, i) => (
                  <option key={d.deviceId || i} value={d.deviceId}>{d.label || `Camera ${i + 1}`}</option>
                ))}
              </select>
            </label>
            {busy && <p className="mt-1 text-xs text-gray-500">Switching camera…</p>}
          </div>
        )}

        {notice && (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            {notice}
          </div>
        )}

        {error && (
          <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
            {error}
          </div>
        )}

        {previewStream ? (
          <button
            type="button"
            onClick={join}
            disabled={busy}
            className="mt-5 inline-flex items-center justify-center px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            Join webinar
          </button>
        ) : (
          <button
            type="button"
            onClick={() => acquire('')}
            disabled={busy}
            className="mt-5 inline-flex items-center justify-center px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            {busy ? 'Requesting camera…' : error ? 'Try again' : 'Enable camera'}
          </button>
        )}
      </div>
    </div>
  );
}

function RecordingIndicator({ status, onRetry, retrying }) {
  // Terminal upload failure gets a real, visible message and an action — a
  // console warning is not an acceptable way to tell a student their
  // attendance evidence stopped being captured.
  if (status === 'failed') {
    return (
      <div
        role="status"
        data-testid="attendance-indicator"
        className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
      >
        <span className="w-2 h-2 rounded-full bg-amber-500 shrink-0" />
        <span className="font-semibold">Attendance recording stopped</span>
        <span className="text-amber-800">
          We couldn&apos;t save your recording. Stay in the webinar and retry.
        </span>
        <button
          type="button"
          onClick={onRetry}
          data-testid="retry-recording"
          disabled={retrying}
          className="ml-auto rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
        >
          {retrying ? 'Retrying…' : 'Retry recording'}
        </button>
      </div>
    );
  }

  const live = status === 'recording';
  const archived = status === 'archived';
  const label =
    archived ? 'Attendance already recorded'
    : live ? 'Recording for attendance audit'
    : status === 'error' ? 'Attendance recording paused'
    : status === 'ended' ? 'Attendance recording complete'
    : 'Connecting…';
  const dot = archived ? 'bg-emerald-500'
            : live ? 'bg-rose-500 animate-pulse'
            : 'bg-gray-400';
  return (
    <div data-testid="attendance-indicator" className="flex items-center gap-2 text-xs text-gray-600">
      <span className={`w-2 h-2 rounded-full ${dot}`} />
      <span>{label}</span>
    </div>
  );
}

export default function AttendanceRecorder({ webinarId, startEndpoint, initialBroadcastState, children }) {
  const [stream, setStream] = useState(null);
  // Authoritative end-of-broadcast signal. The recorder wraps WebinarRoomBody,
  // so the room's own broadcast state lives in a CHILD and can't be read from
  // here — instead we subscribe to the same WebinarPresentationChannel the room
  // uses. `ended` is the only terminal value: `idle` means the publisher is
  // gone but may return (and students join before the broadcast starts), so it
  // must NOT stop attendance capture.
  const [broadcastEnded, setBroadcastEnded] = useState(initialBroadcastState === 'ended');
  // Bumping this tears the recording effect down and builds exactly one fresh
  // recorder — React runs cleanup before setup, so a retry can never leave two
  // MediaRecorders on the same stream.
  const [retryNonce, setRetryNonce] = useState(0);
  const [retrying, setRetrying] = useState(false);
  const [session, setSession] = useState(null); // { id, chunk_url, finalize_url, next_chunk_index }
  const [status, setStatus] = useState('idle'); // idle | recording | error | finalized
  const [bootError, setBootError] = useState(null);

  const recorderRef = useRef(null);
  const indexRef    = useRef(0);
  const finalizedRef = useRef(false);

  // Step 1: ask the server to open a recording session once we have a stream.
  useEffect(() => {
    if (!stream || session) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(startEndpoint, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'X-CSRF-Token': getCsrfToken(), Accept: 'application/json' },
        });
        if (!res.ok) throw new Error(`server returned ${res.status}`);
        const data = await res.json();
        if (cancelled) return;
        indexRef.current = data.next_chunk_index || 0;
        setSession(data);
        // Server says this registration already has a finished recording —
        // don't start MediaRecorder; just let the room render with an
        // informational banner. Recording is one-shot per registration so
        // the audit trail can't be replaced with cleaner footage.
        if (data.already_recorded) setStatus('archived');
      } catch (e) {
        setBootError('Could not start the attendance recording. Reload to try again.');
      }
    })();
    return () => { cancelled = true; };
  }, [stream, session, startEndpoint]);

  useEffect(() => {
    if (!webinarId) return undefined;
    const consumer = getCableConsumer();
    const sub = consumer.subscriptions.create(
      { channel: 'WebinarPresentationChannel', webinar_id: webinarId },
      {
        received: (data) => {
          if (data?.event !== 'broadcast_state') return;
          if (data.state === 'ended') setBroadcastEnded(true);
        },
      },
    );
    return () => sub.unsubscribe();
  }, [webinarId]);

  // Step 2: once we have a session, kick off MediaRecorder. Skip entirely if
  // the recording is already archived from a prior visit.
  useEffect(() => {
    if (!stream || !session || session.already_recorded) return;
    // Nothing to capture once the instructor has stopped for good.
    if (broadcastEnded) { setStatus('ended'); return; }
    const mimeType = pickMimeType();
    if (!mimeType) {
      setBootError('Your browser does not support attendance recording. Please use a recent Chrome, Edge, Firefox, or Safari build.');
      return;
    }
    let stopped = false;
    let allowFinalFlush = false;
    let consecutiveFailures = 0;
    // Aborts in-flight uploads and pending backoffs the moment we shut down.
    const abort = new AbortController();
    let recorder;
    try {
      recorder = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: VIDEO_BITS_PER_SECOND });
    } catch (_e) {
      setBootError('Your browser refused to start the attendance recorder.');
      return;
    }
    recorderRef.current = recorder;

    // Stops recording for good. Safe to call repeatedly — the guard makes the
    // breaker, the cleanup and any final flush converge on one shutdown.
    // `flushFinal` decides what happens to the last chunk MediaRecorder emits
    // from stop(). A normal end-of-broadcast or navigation should keep those
    // bytes; a terminal upload failure should not start yet another doomed
    // upload chain, so it aborts instead.
    const shutdown = ({ flushFinal = true } = {}) => {
      if (stopped) return;
      stopped = true;
      allowFinalFlush = flushFinal;
      if (!flushFinal) abort.abort();
      if (recorder && recorder.state !== 'inactive') {
        try { recorder.stop(); } catch (_e) {}
      }
      // Deliberately NO abort on the flush path: aborting here would cancel the
      // very upload we just chose to keep. `stopped` already blocks any further
      // chunk from being queued, and the single in-flight flush is allowed to
      // finish — best-effort finalization that never blocks navigation.
    };

    recorder.ondataavailable = async (ev) => {
      // stop() emits one last dataavailable. Upload it exactly once when the
      // shutdown was a normal one, then never again.
      if (stopped && !allowFinalFlush) return;
      if (stopped) allowFinalFlush = false;
      if (!ev.data || ev.data.size === 0) return;
      const myIndex = indexRef.current;
      indexRef.current += 1;

      const ok = await uploadChunk(session.chunk_url, myIndex, ev.data, abort.signal);
      if (stopped) return;

      if (ok) {
        consecutiveFailures = 0;
        return;
      }

      consecutiveFailures += 1;
      if (consecutiveFailures >= MAX_CONSECUTIVE_CHUNK_FAILURES) {
        // Terminal for now: stop rather than emitting a new failing chunk every
        // interval forever. Surfaced to the student as a visible banner with a
        // Retry action — never a silent stop.
        setStatus('failed');
        shutdown({ flushFinal: false });
      } else {
        setStatus('error');
      }
    };
    recorder.onerror = () => setStatus('error');
    recorder.onstart = () => setStatus('recording');

    try {
      recorder.start(CHUNK_INTERVAL_MS);
    } catch (_e) {
      setBootError('Could not start the attendance recorder.');
    }

    return shutdown;
  }, [stream, session, broadcastEnded, retryNonce]);

  // Fire finalize on tab close. sendBeacon survives the unload; fetch does not.
  // Skip when the recording is already archived — there's nothing to finalize
  // and the server would just return :no_content.
  useEffect(() => {
    if (!session || session.already_recorded) return;
    const finalize = () => {
      if (finalizedRef.current) return;
      finalizedRef.current = true;
      // sendBeacon will only queue requests whose Content-Type is CORS-
      // safelisted (text/plain, application/x-www-form-urlencoded, multipart/
      // form-data). application/json is NOT safelisted — Chrome silently
      // refuses to queue it, so the tab dies before any finalize call lands.
      // Use text/plain with an empty body since the endpoint doesn't read it.
      try {
        const blob = new Blob([''], { type: 'text/plain' });
        const queued = navigator.sendBeacon(session.finalize_url, blob);
        if (!queued) {
          // Fallback for browsers that refuse the beacon queue: fetch with
          // keepalive can also survive the unload as long as the body is
          // under ~64KB. Ours is empty.
          fetch(session.finalize_url, {
            method: 'POST',
            credentials: 'same-origin',
            keepalive: true,
            headers: { 'Content-Type': 'text/plain' },
          }).catch(() => {});
        }
      } catch (_e) { /* best-effort */ }
    };
    window.addEventListener('pagehide', finalize);
    window.addEventListener('beforeunload', finalize);
    return () => {
      window.removeEventListener('pagehide', finalize);
      window.removeEventListener('beforeunload', finalize);
      // Also fire on normal React unmount (navigating to /webinars etc.).
      finalize();
    };
  }, [session]);

  // Stop the camera when we tear down so the indicator light goes off.
  useEffect(() => {
    return () => {
      stream?.getTracks().forEach((t) => { try { t.stop(); } catch (_e) {} });
    };
  }, [stream]);

  // One fresh recorder, never two: bumping the nonce re-runs the recording
  // effect, and React tears the previous one down (shutdown) before setting the
  // next one up. `retrying` disables the button so repeated clicks can't stack
  // restarts, and it clears once the new recorder reports itself recording.
  const handleRetryRecording = useCallback(() => {
    if (retrying) return;
    setRetrying(true);
    setStatus('idle');
    setRetryNonce((n) => n + 1);
  }, [retrying]);

  useEffect(() => {
    if (retrying && (status === 'recording' || status === 'failed')) setRetrying(false);
  }, [status, retrying]);

  if (!stream) {
    return <CameraSetup onJoin={setStream} />;
  }

  if (bootError) {
    return (
      <div className="max-w-xl mx-auto p-6">
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6 text-sm text-rose-800">
          {bootError}
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="max-w-xl mx-auto p-6 text-sm text-gray-500">Preparing attendance recorder…</div>
    );
  }

  // Handed to the room rather than rendered above it: as a sibling before
  // Layout the indicator sits in the strip the sticky mobile header pins over,
  // so scrolling hides it. The room places it inside its own content column.
  // `children` is a render callback, so a caller that forgets the indicator
  // fails loudly instead of dropping it.
  return children(
    <RecordingIndicator status={status} onRetry={handleRetryRecording} retrying={retrying} />,
  );
}
